import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '@/components';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { useQueryPanelStore } from '@/stores';
import {
    dataUrlToBlob,
    downloadDataUrlAsFile,
    extractCroppedVideoFrame,
    getVideoElementFromRef,
    videoElementRectToFrameRegion,
    VideoFrameRegion,
} from '@/utils/video-player';
import { useVideoPlayerContext } from './VideoPlayerProvider';
import { VideoCropperActionsPopover } from './VideoCropperActionsPopover';
import {
    SnapshotAnimationRect,
    VideoCropSnapshotAnimation,
} from './VideoCropSnapshotAnimation';

const ATTACHMENT_ANCHOR_SELECTOR = '[data-query-panel-attachment-anchor]';
const ATTACHMENT_LAYOUT_SETTLE_MS = 320;
const ATTACHMENT_ANCHOR_POLL_INTERVAL_MS = 32;
const ATTACHMENT_ANCHOR_POLL_TIMEOUT_MS = 400;
const ATTACHMENT_FALLBACK_SIZE_PX = 72;
const ATTACHMENT_FALLBACK_BOTTOM_OFFSET_PX = 140;

interface SelectionRectangle {
    left: number;
    top: number;
    width: number;
    height: number;
}

const MIN_SELECTION_SIZE_PIXELS = 8;
const POPOVER_GAP_PIXELS = 12;

const clamp = (value: number, min: number, max: number): number =>
    Math.max(min, Math.min(value, max));

const isSelectionUsable = (rectangle: SelectionRectangle): boolean =>
    rectangle.width >= MIN_SELECTION_SIZE_PIXELS &&
    rectangle.height >= MIN_SELECTION_SIZE_PIXELS;

const computePopoverCenterX = (
    desiredCenterX: number,
    leftBound: number,
    rightBound: number,
    popoverWidth: number,
): number => {
    const halfWidth = popoverWidth / 2;
    const minCenter = leftBound + halfWidth;
    const maxCenter = rightBound - halfWidth;
    if (popoverWidth === 0 || maxCenter <= minCenter) {
        return (leftBound + rightBound) / 2;
    }
    return clamp(desiredCenterX, minCenter, maxCenter);
};

const buildFallbackAttachmentRect = (): SnapshotAnimationRect => ({
    left: window.innerWidth / 2 - ATTACHMENT_FALLBACK_SIZE_PX / 2,
    top: window.innerHeight - ATTACHMENT_FALLBACK_BOTTOM_OFFSET_PX,
    width: ATTACHMENT_FALLBACK_SIZE_PX,
    height: ATTACHMENT_FALLBACK_SIZE_PX,
});

const measureAttachmentAnchor = (): SnapshotAnimationRect | null => {
    const element = document.querySelector(ATTACHMENT_ANCHOR_SELECTOR);
    if (!(element instanceof HTMLElement)) return null;
    const rect = element.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return null;
    return {
        left: rect.left,
        top: rect.top,
        width: rect.width,
        height: rect.height,
    };
};

const waitForAttachmentAnchor = async (): Promise<SnapshotAnimationRect> => {
    await new Promise<void>((resolve) => {
        window.setTimeout(resolve, ATTACHMENT_LAYOUT_SETTLE_MS);
    });
    const start = performance.now();
    return new Promise<SnapshotAnimationRect>((resolve) => {
        const poll = () => {
            const rect = measureAttachmentAnchor();
            if (rect) {
                resolve(rect);
                return;
            }
            if (
                performance.now() - start >=
                ATTACHMENT_ANCHOR_POLL_TIMEOUT_MS
            ) {
                resolve(buildFallbackAttachmentRect());
                return;
            }
            window.setTimeout(poll, ATTACHMENT_ANCHOR_POLL_INTERVAL_MS);
        };
        poll();
    });
};

const captureCropFile = async (
    videoElement: HTMLVideoElement,
    region: VideoFrameRegion,
): Promise<{ file: File; dataUrl: string }> => {
    const dataUrl = await extractCroppedVideoFrame(videoElement, region);
    const blob = dataUrlToBlob(dataUrl);
    const file = new File([blob], `crop-${Date.now()}.png`, {
        type: blob.type || 'image/png',
    });
    return { file, dataUrl };
};

export const VideoCropper: React.FC = () => {
    const { t } = useTranslation(I18nNamespace.VIDEO);
    const {
        containerRef,
        activeMediaRef,
        cropEnabled,
        croppingMode,
        setCroppingMode,
        expanded,
        toggleExpanded,
        zoomed,
    } = useVideoPlayerContext();

    const [selection, setSelection] = useState<SelectionRectangle | null>(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [isAttachingToQuery, setIsAttachingToQuery] = useState(false);
    const [snapshot, setSnapshot] = useState<{
        sourceRect: SnapshotAnimationRect;
        targetRect: SnapshotAnimationRect;
        imageSrc: string;
    } | null>(null);
    const [popoverWidth, setPopoverWidth] = useState(0);
    const [, bumpLayoutTick] = useState(0);
    const dragStartRef = useRef<{ x: number; y: number } | null>(null);
    const popoverRef = useRef<HTMLDivElement | null>(null);
    const uploadOutcomeRef = useRef<{ done: boolean; success: boolean }>({
        done: false,
        success: false,
    });

    const showPopover = selection !== null && !isDrawing;

    useEffect(() => {
        if (!croppingMode) {
            setSelection(null);
            setIsDrawing(false);
            dragStartRef.current = null;
        }
    }, [croppingMode]);

    useEffect(() => {
        if (popoverRef.current) {
            setPopoverWidth(popoverRef.current.offsetWidth);
        }
    }, [selection, isDrawing]);

    useEffect(() => {
        if (!croppingMode) return;
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key !== 'Escape') return;
            event.preventDefault();
            event.stopPropagation();
            setCroppingMode(false);
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [croppingMode, setCroppingMode]);

    useEffect(() => {
        if (!showPopover) return;
        const handleViewportChange = () => bumpLayoutTick((tick) => tick + 1);
        window.addEventListener('scroll', handleViewportChange, true);
        window.addEventListener('resize', handleViewportChange);
        return () => {
            window.removeEventListener('scroll', handleViewportChange, true);
            window.removeEventListener('resize', handleViewportChange);
        };
    }, [showPopover]);

    const getContainerLocalCoords = (clientX: number, clientY: number) => {
        const container = containerRef.current;
        if (!container) return null;
        const rect = container.getBoundingClientRect();
        return {
            x: clamp(clientX - rect.left, 0, container.clientWidth),
            y: clamp(clientY - rect.top, 0, container.clientHeight),
        };
    };

    const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
        if (event.button !== 0) return;
        event.preventDefault();
        event.stopPropagation();
        const coords = getContainerLocalCoords(event.clientX, event.clientY);
        if (!coords) return;
        dragStartRef.current = coords;
        setSelection({ left: coords.x, top: coords.y, width: 0, height: 0 });
        setIsDrawing(true);
        event.currentTarget.setPointerCapture(event.pointerId);
    };

    const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
        if (!isDrawing) return;
        const start = dragStartRef.current;
        const coords = getContainerLocalCoords(event.clientX, event.clientY);
        if (!start || !coords) return;
        setSelection({
            left: Math.min(start.x, coords.x),
            top: Math.min(start.y, coords.y),
            width: Math.abs(coords.x - start.x),
            height: Math.abs(coords.y - start.y),
        });
    };

    const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
        if (!isDrawing) return;
        event.stopPropagation();
        event.currentTarget.releasePointerCapture(event.pointerId);
        dragStartRef.current = null;
        setIsDrawing(false);
        setSelection((previous) =>
            previous && isSelectionUsable(previous) ? previous : null,
        );
    };

    const resolveVideoElement = (
        failureMessage: string,
    ): HTMLVideoElement | null => {
        const element = getVideoElementFromRef(activeMediaRef);
        if (element instanceof HTMLVideoElement) return element;
        toast.error(failureMessage);
        return null;
    };

    const handleDownload = async () => {
        if (!selection) return;
        const videoElement = resolveVideoElement(t('crop.downloadFailed'));
        if (!videoElement) return;

        const region = videoElementRectToFrameRegion(videoElement, selection);
        if (!region) {
            toast.error(t('crop.downloadFailed'));
            return;
        }

        setIsDownloading(true);
        try {
            const dataUrl = await extractCroppedVideoFrame(
                videoElement,
                region,
            );
            downloadDataUrlAsFile(dataUrl, `frame-${Date.now()}.png`);
            setCroppingMode(false);
        } catch (error) {
            console.error('Crop download failed:', error);
            toast.error(t('crop.downloadFailed'));
        } finally {
            setIsDownloading(false);
        }
    };

    const handleAttachToQuery = async () => {
        if (!selection || isAttachingToQuery || isDownloading) return;
        const queryPanel = useQueryPanelStore.getState();
        const videoElement = resolveVideoElement(t('crop.attachFailed'));
        if (!videoElement) return;
        if (queryPanel.isUploading) {
            toast.error(t('crop.attachFailed'));
            return;
        }

        const region = videoElementRectToFrameRegion(videoElement, selection);
        const containerRect = containerRef.current?.getBoundingClientRect();
        if (!region || !containerRect) {
            toast.error(t('crop.attachFailed'));
            return;
        }

        setIsAttachingToQuery(true);

        const sourceRect: SnapshotAnimationRect = {
            left: containerRect.left + selection.left,
            top: containerRect.top + selection.top,
            width: selection.width,
            height: selection.height,
        };

        try {
            if (expanded) {
                toggleExpanded();
            }
            if (queryPanel.isMinimized) {
                queryPanel.expand();
            }

            const { file, dataUrl } = await captureCropFile(
                videoElement,
                region,
            );

            queryPanel.setAttachmentSnapshotHidden(true);
            queryPanel.clearAttachmentFile();

            uploadOutcomeRef.current = { done: false, success: false };
            void queryPanel.attachFile(file).then((success) => {
                uploadOutcomeRef.current = { done: true, success };
            });

            const targetRect = await waitForAttachmentAnchor();

            // If the upload already failed during the layout-settle window,
            // the attachment row has unmounted and attachFile has surfaced its
            // own error toast. Skip the optimistic fly-in so we don't animate
            // the snapshot to a stale/fallback target as though the attach
            // succeeded.
            if (
                uploadOutcomeRef.current.done &&
                !uploadOutcomeRef.current.success
            ) {
                queryPanel.setAttachmentSnapshotHidden(false);
                setIsAttachingToQuery(false);
                setCroppingMode(false);
                return;
            }

            setSnapshot({ sourceRect, targetRect, imageSrc: dataUrl });
            setCroppingMode(false);
        } catch (error) {
            console.error('Crop attach failed:', error);
            toast.error(t('crop.attachFailed'));
            queryPanel.setAttachmentSnapshotHidden(false);
            setIsAttachingToQuery(false);
        }
    };

    const handleSnapshotComplete = useCallback(() => {
        useQueryPanelStore.getState().setAttachmentSnapshotHidden(false);
        setSnapshot(null);
        setIsAttachingToQuery(false);
    }, []);

    useEffect(() => {
        return () => {
            useQueryPanelStore.getState().setAttachmentSnapshotHidden(false);
        };
    }, []);

    if (!cropEnabled || zoomed) return null;

    if (!croppingMode) {
        if (!snapshot) return null;
        return (
            <VideoCropSnapshotAnimation
                sourceRect={snapshot.sourceRect}
                targetRect={snapshot.targetRect}
                imageSrc={snapshot.imageSrc}
                onComplete={handleSnapshotComplete}
            />
        );
    }

    const containerRect = containerRef.current?.getBoundingClientRect() ?? null;
    const popoverViewportStyle =
        showPopover && selection && containerRect
            ? {
                  left: computePopoverCenterX(
                      containerRect.left + selection.left + selection.width / 2,
                      containerRect.left,
                      containerRect.right,
                      popoverWidth,
                  ),
                  top: containerRect.top + selection.top - POPOVER_GAP_PIXELS,
                  transform: 'translate(-50%, -100%)',
              }
            : null;

    return (
        <div
            className="absolute inset-0 z-30 cursor-crosshair select-none"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            onClick={(event) => event.stopPropagation()}
            onContextMenu={(event) => event.preventDefault()}
        >
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                {selection ? (
                    <div
                        className="absolute border border-white-100"
                        style={{
                            left: selection.left,
                            top: selection.top,
                            width: selection.width,
                            height: selection.height,
                            boxShadow: '0 0 0 9999px rgba(7,8,10,0.5)',
                        }}
                    />
                ) : (
                    <>
                        <div className="absolute inset-0 bg-black-100/40" />
                        {!isDrawing && (
                            <>
                                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-[linear-gradient(to_top,rgba(17,18,20,0.6)_0%,rgba(17,18,20,0.25)_32%,rgba(17,18,20,0.07)_65%,transparent_100%)]" />
                                <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-sm font-medium text-white-100">
                                    {t('crop.dragToSelectArea')}
                                </div>
                            </>
                        )}
                    </>
                )}
            </div>
            {popoverViewportStyle && !snapshot && (
                <VideoCropperActionsPopover
                    ref={popoverRef}
                    isDownloading={isDownloading}
                    isAttachingToQuery={isAttachingToQuery}
                    onDownload={handleDownload}
                    onAttachToQuery={handleAttachToQuery}
                    style={popoverViewportStyle}
                />
            )}
            {snapshot && (
                <VideoCropSnapshotAnimation
                    sourceRect={snapshot.sourceRect}
                    targetRect={snapshot.targetRect}
                    imageSrc={snapshot.imageSrc}
                    onComplete={handleSnapshotComplete}
                />
            )}
        </div>
    );
};
